import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import SupervisorComparative from "./SupervisorComparative";
import {
  Loader2, Download, ClipboardCheck, Target, Timer, Activity,
  Search, ArrowDownToLine, AlertTriangle, TrendingDown, TrendingUp, Minus,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reporte de Tiempos de Renovaciones (basado en Excel REPORTE REGISTRO DE RENOVACIONES).
 * Muestra KPIs (% meta ≤20 días, promedio días vigencia vs facturación, promedio en digitación)
 * + tabla detallada por póliza con color coding y exportación XLSX 1:1 del Excel cliente.
 */
export default function RenovacionesTiemposReport({ mesesVencimiento = [], filtroVenceEn = "todos" }) {
  const { user } = useAuth();
  const isAdmin = useMemo(() => {
    const r = (user?.role || "").toLowerCase();
    return r === "admin" || r === "administrador" || r === "superadmin";
  }, [user]);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const [data, setData] = useState({ kpis: null, items: [] });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [search, setSearch] = useState("");
  const [onlyRenovadas, setOnlyRenovadas] = useState(false);
  const [ejecutivoFilter, setEjecutivoFilter] = useState("all");
  const [localMes, setLocalMes] = useState(filtroVenceEn || "todos");
  const [vistaSupervisor, setVistaSupervisor] = useState("");
  const [vistaSupervisorOpciones, setVistaSupervisorOpciones] = useState([]);

  useEffect(() => { setLocalMes(filtroVenceEn || "todos"); }, [filtroVenceEn]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/estadisticas/renovaciones/vista-supervisor/opciones`, authHeaders());
        setVistaSupervisorOpciones(r.data?.opciones || []);
      } catch {}
    })();
  }, [authHeaders]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("mes_vencimiento", localMes === "todos" ? "all" : localMes);
      if (onlyRenovadas) params.set("only_renovadas", "true");
      if (ejecutivoFilter !== "all") params.set("ejecutivo", ejecutivoFilter);
      if (vistaSupervisor) params.set("vista_supervisor_id", vistaSupervisor);
      const r = await axios.get(`${API_URL}/api/estadisticas/renovaciones/reporte-tiempos?${params.toString()}`, authHeaders());
      setData(r.data || { kpis: null, items: [] });
    } catch (e) {
      toast.error("No se pudo cargar el reporte de tiempos");
    }
    setLoading(false);
  }, [localMes, onlyRenovadas, ejecutivoFilter, vistaSupervisor, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set("mes_vencimiento", localMes === "todos" ? "all" : localMes);
      if (onlyRenovadas) params.set("only_renovadas", "true");
      if (ejecutivoFilter !== "all") params.set("ejecutivo", ejecutivoFilter);
      if (vistaSupervisor) params.set("vista_supervisor_id", vistaSupervisor);
      const r = await axios.get(
        `${API_URL}/api/estadisticas/renovaciones/reporte-tiempos/excel?${params.toString()}`,
        { ...authHeaders(), responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_renovaciones_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte exportado");
    } catch (e) {
      toast.error("No se pudo generar el Excel");
    }
    setDownloading(false);
  };

  const ejecutivos = useMemo(() => {
    const set = new Set();
    for (const r of data.items || []) if (r.ejecutivo) set.add(r.ejecutivo);
    return Array.from(set).sort();
  }, [data.items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.items || [];
    return (data.items || []).filter((r) =>
      [r.poliza, r.cliente, r.compania, r.ejecutivo, r.ramo]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [data.items, search]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  // Color coding según lo pedido:
  // ≤ -15  → azul claro
  // -10..-14 → naranja claro
  // ≥ -9 (incluye positivos) → rojo
  const diffBadge = (diff) => {
    if (diff === null || diff === undefined) {
      return <span className="text-slate-300 text-xs">—</span>;
    }
    let cls = "";
    let Icon = Minus;
    if (diff <= -15) { cls = "bg-blue-100 text-blue-700 border-blue-300"; Icon = TrendingDown; }
    else if (diff <= -10) { cls = "bg-orange-100 text-orange-700 border-orange-300"; Icon = AlertTriangle; }
    else { cls = "bg-rose-100 text-rose-700 border-rose-300"; Icon = TrendingUp; }
    return (
      <Badge variant="outline" className={`text-[11px] px-2 py-0.5 font-semibold ${cls}`}>
        <Icon className="w-3 h-3 mr-1" />
        {diff}
      </Badge>
    );
  };

  /**
   * Clase Tailwind para el badge de STATUS basada en la diferencia de días.
   * Usa `diferencia_recibido_vigencia` (preferido); si está nulo, cae a
   * `diferencia_facturado_vigencia` para colorear igualmente la mayoría de
   * filas según las reglas pedidas:
   *   - ≤ -15 (entregado bastante antes del inicio)  → azul claro
   *   - -10 a -14 (entregado un poco antes, atención) → naranja claro
   *   - ≥ -9 (incluye tardío o positivo, crítico)     → rojo
   */
  const statusBadgeClass = (row) => {
    const diff = row.diferencia_recibido_vigencia ?? row.diferencia_facturado_vigencia;
    if (diff === null || diff === undefined) {
      return "bg-slate-100 text-slate-600 border-slate-200";
    }
    if (diff <= -15) return "bg-blue-100 text-blue-700 border-blue-300";
    if (diff <= -10) return "bg-orange-100 text-orange-700 border-orange-300";
    return "bg-rose-100 text-rose-700 border-rose-300";
  };

  const kpis = data.kpis || {};

  return (
    <div className="space-y-5" data-testid="renovaciones-tiempos-section">
      {/* Header con título + botón export */}
      <div className="flex items-start justify-between gap-3 flex-wrap pb-2 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h3 className="text-lg font-bold font-['Manrope'] tracking-tight text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-teal-600" />
            Reporte de Tiempos de Renovaciones
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Tiempos de ciclo de renovación · Datos en vivo desde Control de Renovaciones y Mensajería
          </p>
        </div>
        <Button
          onClick={handleExportExcel}
          disabled={downloading || loading}
          className="bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white shadow-md"
          data-testid="export-reporte-renovaciones-btn"
        >
          {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowDownToLine className="w-4 h-4 mr-2" />}
          Exportar reporte a Excel
        </Button>
      </div>

      {/* KPIs grandes (replican el encabezado del Excel) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
          <CardContent className="py-5 px-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] uppercase tracking-wider font-bold opacity-90">Reporte de tiempo · Meta ≤20 días</p>
              <Target className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-3xl font-bold">{kpis.meta_20_dias_pct ?? 0}%</p>
            <p className="text-[11px] opacity-80 mt-1">
              {kpis.meta_20_dias_count ?? 0} de {kpis.meta_20_dias_total_calc ?? 0} renovaciones bajo meta
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-amber-500 to-orange-600 text-white">
          <CardContent className="py-5 px-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] uppercase tracking-wider font-bold opacity-90">Promedio días vigencia vs facturación</p>
              <Activity className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-3xl font-bold">{kpis.promedio_dias_vigencia_vs_facturacion ?? 0}</p>
            <p className="text-[11px] opacity-80 mt-1">Días promedio</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
          <CardContent className="py-5 px-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] uppercase tracking-wider font-bold opacity-90">Promedio días en digitación</p>
              <Timer className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-3xl font-bold">{kpis.promedio_tiempo_digitacion ?? 0}</p>
            <p className="text-[11px] opacity-80 mt-1">Recibida en oficina → Facturado Korrenetz</p>
          </CardContent>
        </Card>
      </div>

      {/* Comparativa entre supervisores (SOLO ADMIN) */}
      {isAdmin && (
        <SupervisorComparative
          mesVencimiento={localMes}
          onlyRenovadas={onlyRenovadas}
        />
      )}

      {/* Filtros locales */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardContent className="py-3 px-4">
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por póliza, cliente, ejecutivo…"
                className="pl-8 h-9 text-sm"
                data-testid="renov-tiempos-search"
              />
            </div>
            <Select value={localMes} onValueChange={setLocalMes}>
              <SelectTrigger className="w-full md:w-48 h-9 text-sm" data-testid="renov-tiempos-mes-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los meses</SelectItem>
                {(mesesVencimiento || []).map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={ejecutivoFilter} onValueChange={setEjecutivoFilter}>
              <SelectTrigger className="w-full md:w-52 h-9 text-sm" data-testid="renov-tiempos-ejec-select"><SelectValue placeholder="Ejecutivo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los ejecutivos</SelectItem>
                {ejecutivos.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
            {vistaSupervisorOpciones.length > 0 && (
              <Select value={vistaSupervisor || "PROPIA"} onValueChange={(v) => setVistaSupervisor(v === "PROPIA" ? "" : v)}>
                <SelectTrigger className="w-full md:w-52 h-9 text-sm" data-testid="renov-tiempos-vista-supervisor-select"><SelectValue placeholder="Vista Supervisor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PROPIA">Vista Supervisor: Mi vista</SelectItem>
                  {vistaSupervisorOpciones.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <label className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 cursor-pointer text-xs">
              <input
                type="checkbox"
                checked={onlyRenovadas}
                onChange={(e) => setOnlyRenovadas(e.target.checked)}
                className="h-3.5 w-3.5 rounded text-teal-600"
                data-testid="renov-tiempos-only-renovadas"
              />
              <span className="font-medium text-slate-700 dark:text-slate-200">Sólo RENOVADA</span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Leyenda color coding */}
      <Card className="border-slate-200 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40">
        <CardContent className="py-2.5 px-4">
          <div className="flex items-center gap-4 flex-wrap text-[11px]">
            <span className="text-slate-500 font-semibold uppercase tracking-wide text-[10px]">Diferencia días recibido / vigencia:</span>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-4 h-4 rounded bg-blue-200 border border-blue-300" />
              <span className="text-slate-700 dark:text-slate-300">≤ -15 días (excelente)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-4 h-4 rounded bg-orange-200 border border-orange-300" />
              <span className="text-slate-700 dark:text-slate-300">-10 a -14 días (atención)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-4 h-4 rounded bg-rose-200 border border-rose-300" />
              <span className="text-slate-700 dark:text-slate-300">≥ -9 días o positivo (crítico)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla detalle */}
      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
        <CardHeader className="py-3 px-4 bg-slate-50 dark:bg-slate-900/60">
          <CardTitle className="text-sm font-semibold flex items-center justify-between">
            <span>Detalle por póliza ({filtered.length} de {data.items?.length || 0})</span>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[620px]">
            <table className="w-full text-[11px]" data-testid="renov-tiempos-table">
              <thead className="bg-slate-800 text-white sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 text-left whitespace-nowrap">Póliza</th>
                  <th className="px-2 py-2 text-left whitespace-nowrap">Cliente</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">Recibido en oficina</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">Facturado Korrenetz</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">T. digitación</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">Dif. facturado / vigencia</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">Recibido por cliente</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">Días vigencia vs fact.</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">Dif. recibido / vigencia</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">Inicio Vigencia</th>
                  <th className="px-2 py-2 text-left whitespace-nowrap">Ejecutivo</th>
                  <th className="px-2 py-2 text-left whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.length === 0 && !loading && (
                  <tr><td colSpan={12} className="text-center text-slate-400 py-8">Sin renovaciones para mostrar</td></tr>
                )}
                {filtered.slice(0, 500).map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`renov-tiempos-row-${r.id}`}>
                    <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.poliza}</td>
                    <td className="px-2 py-1.5 max-w-[220px] truncate" title={r.cliente}>{r.cliente}</td>
                    <td className="px-2 py-1.5 text-center whitespace-nowrap">{formatDate(r.fecha_recibida_oficina)}</td>
                    <td className="px-2 py-1.5 text-center whitespace-nowrap">{formatDate(r.fecha_facturado_korrenetz)}</td>
                    <td className="px-2 py-1.5 text-center font-semibold">{r.tiempo_digitacion ?? "—"}</td>
                    <td className="px-2 py-1.5 text-center">{r.diferencia_facturado_vigencia ?? "—"}</td>
                    <td className="px-2 py-1.5 text-center whitespace-nowrap">{formatDate(r.fecha_recibido_cliente)}</td>
                    <td className="px-2 py-1.5 text-center">{r.dias_vigencia_vs_facturacion ?? "—"}</td>
                    <td className="px-2 py-1.5 text-center">{diffBadge(r.diferencia_recibido_vigencia)}</td>
                    <td className="px-2 py-1.5 text-center whitespace-nowrap text-slate-600 dark:text-slate-300 font-medium">{formatDate(r.inicio_vigencia)}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap truncate max-w-[140px]" title={r.ejecutivo}>{r.ejecutivo}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <Badge variant="outline" className={`text-[9px] px-1.5 py-0 font-semibold ${statusBadgeClass(r)}`} data-testid={`renov-tiempos-status-${r.id}`}>{r.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length > 500 && (
            <div className="px-4 py-2 text-[11px] text-slate-500 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40">
              Mostrando 500 de {filtered.length} filas. Exportá a Excel para ver el listado completo.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
